import { ERROR_MESSAGES_RU, type FieldError } from "@cafe/shared";
import { formatMoney } from "@cafe/ui";
import i18n from "@/shared/i18n";
import { translateValidation } from "@/shared/i18n/validation";
import { featureLabel, permissionActionLabel, permissionResourceLabel, unitLabel } from "@/shared/lib/options";
import type { ApiError } from "./client";

// Простая сигнатура: на большом словаре полная типизация ключей упирается в глубину типов TS
const t = (key: string, o?: Record<string, unknown>) => (i18n.t as (k: string, o?: Record<string, unknown>) => string)(key, o);
const money = (v: unknown) => formatMoney(Number(v ?? 0));
const qty = (v: unknown, unit: unknown) => `${(Math.max(0, Math.round(Number(v ?? 0) * 1000) / 1000)).toLocaleString(i18n.language)} ${unit ? unitLabel(String(unit)) : ""}`.trim();
const perm = (code: string) => {
  const [res = code, act = ""] = code.split(".");
  return act ? `${permissionResourceLabel(res, res)} — ${permissionActionLabel(act, act)}` : code;
};

/** Ошибки полей с сервера → «Строка 2: Больше 0» (переведённые, без повторов). */
export function fieldErrorTexts(fields: FieldError[]): string[] {
  const out = fields.map((f) => {
    const idx = /\[(\d+)\]|\.(\d+)(?:\.|$)/.exec(f.path);
    const n = idx ? Number(idx[1] ?? idx[2]) + 1 : null;
    const msg = translateValidation(f.message);
    return n ? `${t("errors.row", { n })}: ${msg}` : msg;
  });
  return [...new Set(out.filter(Boolean))];
}

/** Подробности из `details` по коду ошибки — на языке интерфейса. */
function detailTexts(e: ApiError): string[] {
  const d = (e.details ?? {}) as Record<string, unknown>;
  switch (e.code) {
    case "INSUFFICIENT_STOCK": {
      if (!d.productName) return [];
      const o = { product: d.productName, warehouse: d.warehouseName, available: qty(d.availableQty, d.unit), required: qty(d.requiredQty, d.unit) };
      return [t(d.warehouseName ? "errors.detail.stockAt" : "errors.detail.stock", o)];
    }
    case "CREDIT_LIMIT_EXCEEDED":
      return [d.creditAllowed === false ? t("errors.detail.creditOff") : t("errors.detail.credit", { limit: money(d.limit), balance: money(d.balance), amount: money(d.amount) })];
    case "CASH_INSUFFICIENT":
      return [t("errors.detail.cash", { expected: money(d.expectedMinor), amount: money(d.amountMinor) })];
    case "PAYMENT_AMOUNT_INVALID":
      return d.reason === "deposit" ? [t("errors.detail.depositLeft", { available: money(d.availableMinor) })] : d.dueMinor !== undefined ? [t("errors.detail.due", { due: money(d.dueMinor) })] : [];
    case "REFUND_EXCEEDS_PAID":
      return d.availableMinor !== undefined ? [t("errors.detail.refund", { available: money(d.availableMinor) })] : [];
    case "DEPOSIT_REQUIRED":
      return [t("errors.detail.deposit", { required: money(d.requiredMinor), paid: money(d.paidMinor) })];
    case "DISCOUNT_INVALID":
      return d.baseMinor !== undefined ? [t("errors.detail.discount", { base: money(d.baseMinor) })] : [];
    case "MENU_ITEM_UNAVAILABLE": {
      const names = ((d.items as { name?: string }[] | undefined) ?? []).map((i) => i.name).filter(Boolean);
      return names.length ? [t("errors.detail.unavailable", { items: names.join(", ") })] : [];
    }
    case "MODIFIERS_INVALID": {
      const problems = (d.problems as string[] | undefined) ?? [];
      return problems.length ? [`${d.item ? `${String(d.item)}: ` : ""}${problems.join("; ")}`] : [];
    }
    case "PERMISSION_DENIED": {
      const codes = ((d.required ?? d.anyOf) as string[] | undefined) ?? [];
      return codes.length ? [t("errors.detail.permission", { perms: codes.map(perm).join(", ") })] : [];
    }
    case "PIN_LOCKED":
    case "RATE_LIMITED":
    case "ACCOUNT_LOCKED": {
      const sec = typeof d.retryAfterSec === "number" ? d.retryAfterSec : typeof d.until === "string" ? (Date.parse(d.until) - Date.now()) / 1000 : 0;
      return sec > 0 ? [t("errors.detail.retry", { min: Math.max(1, Math.ceil(sec / 60)) })] : [];
    }
    case "QUOTA_EXCEEDED":
      return d.needed !== undefined ? [t("errors.detail.quota", { needed: d.needed, remaining: d.remaining })] : [];
    case "UNIT_CONVERSION_UNDEFINED":
      return d.product ? [t("errors.detail.unit", { product: d.product, unit: unitLabel(String(d.unit)), base: unitLabel(String(d.baseUnit)) })] : [];
    case "PLAN_LIMIT_REACHED":
      return d.max !== undefined ? [t("errors.detail.limit", { max: d.max })] : [];
    case "FEATURE_NOT_IN_PLAN":
      return typeof d.feature === "string" ? [featureLabel(d.feature)] : [];
    default:
      return [];
  }
}

/**
 * Понятный текст ошибки: причина, а не «Проверьте правильность заполнения полей».
 * Сервер пишет на ru: свой текст (с названиями и суммами) показываем как есть, общий по коду — переводим
 * и дополняем подробностями из `details`; ошибки валидации — текстами полей.
 */
export function describeApiError(e: ApiError): string {
  const ru = i18n.language === "ru";
  const generic = !e.message || e.message === ERROR_MESSAGES_RU[e.code as keyof typeof ERROR_MESSAGES_RU] || e.message === e.code;
  const key = `errors.${e.code}`;
  const base = generic || !ru ? (i18n.exists(key) ? t(key) : e.message) : e.message;
  const fields = e.code === "TOTALS_MISMATCH" ? [] : fieldErrorTexts(e.fields);
  if (e.code === "VALIDATION_ERROR" && fields.length) return fields.join(". ");
  const extra = generic || !ru ? detailTexts(e) : [];
  return [base, ...extra, ...fields.filter((f) => f !== base)].join(". ");
}
